/**
 * CaptureScreen — the REAL-BROWSER capture page served at
 * https://davomat.oneplusone.uz/capture?t=<token>.
 *
 * It is NOT the Telegram Mini App: it runs in the system/in-app browser opened
 * via WebApp.openLink from the Landing screen, has NO Telegram SDK and NO
 * initData, and authenticates purely by the single-use punch token in ?t=.
 * getUserMedia — blocked in the Telegram webview — WORKS here (confirmed on
 * device), which is the whole reason this page exists.
 *
 * FLOW
 *   1. read token from ?t=            (missing → TOKEN_MISSING)
 *   2. GPS via navigator.geolocation  (denied/unavailable → mapped error)
 *   3. front camera via getUserMedia  (facingMode:'user' → <video> + oval)
 *   4. POST web_challenge{token}       → liveness cue (shown big + arrow)
 *   5. lazy-load MediaPipe FaceLandmarker; per-frame yaw/pitch; calibrate a
 *      neutral baseline, then AUTO-CAPTURE when the cued head movement crosses
 *      threshold — or after a ~5 s fallback.
 *   6. downscale the frame → jpeg base64; POST web_punch{token,lat,lng,accuracy,
 *      cue,image_b64}; success seal + "Telegramga qaytish", or a mapped error.
 *
 * The server (web_punch) consumes the token and runs the SAME punch + sidecar
 * match/anti-spoof + GPS-radius gate as the native checkin/checkout — this page
 * only decides WHEN to snap the still.
 */
import { useEffect, useRef, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Spinner from '../components/Spinner.jsx';
import Button from '../components/Button.jsx';
import { T, cueText, cueArrow } from '../lib/i18n.js';
import { errorUI } from '../lib/errors.js';
import api from '../lib/api.js';
import { createFaceTracker, yawPitch, matchesCue } from '../lib/headpose.js';

// ---- tuning -----------------------------------------------------------------
const BASELINE_MS = 700;        // min time to average a neutral head pose
const BASELINE_MIN_SAMPLES = 8; // min face frames before baseline is trusted
const MOVE_FALLBACK_MS = 5000;  // snap anyway this long after the cue is live
const HARD_CAP_MS = 9000;       // snap anyway (no face / no baseline) safety net
const MAX_EDGE = 720;           // downscale so the POST body stays small
const JPEG_Q = 0.8;

function getToken() {
  try {
    return new URLSearchParams(window.location.search).get('t') || '';
  } catch (_e) {
    return '';
  }
}

/** Try hard to return the worker to Telegram; degrade to a plain tab close. */
function backToTelegram() {
  try {
    if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.close) {
      window.Telegram.WebApp.close();
      return;
    }
  } catch (_e) { /* not a Mini App context — expected here */ }
  try { window.close(); } catch (_e) { /* may be blocked; user taps the X */ }
}

export default function CaptureScreen() {
  const videoRef = useRef(null);

  const [phase, setPhase] = useState('init'); // init | tracking | sending | done | error
  const [status, setStatus] = useState(T.cap_getting_location);
  const [cue, setCue] = useState(null);
  const [errCode, setErrCode] = useState(null);
  const [result, setResult] = useState(null); // { isIn, name }

  // Long-lived refs (never trigger re-render / never re-created by the loop).
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(0);
  const capturedRef = useRef(false);   // one-shot capture guard
  const consumedRef = useRef(false);   // token spent (web_punch fired)
  const tokenRef = useRef('');
  const geoRef = useRef(null);
  const cueRef = useRef(null);
  const baseRef = useRef(null);        // { yaw, pitch } neutral baseline
  const baseAcc = useRef({ yaw: 0, pitch: 0, n: 0, startedAt: 0 });
  const trackStartRef = useRef(0);
  const moveStartRef = useRef(0);      // when the cue went live (baseline done)
  const aliveRef = useRef(true);

  function stopCamera() {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    const s = streamRef.current;
    if (s) { try { s.getTracks().forEach((t) => t.stop()); } catch (_e) { /**/ } }
    streamRef.current = null;
    const lm = landmarkerRef.current;
    if (lm) { try { lm.close(); } catch (_e) { /**/ } }
    landmarkerRef.current = null;
  }

  function fail(code) {
    if (!aliveRef.current) return;
    stopCamera();
    setErrCode(code || 'SERVER_ERROR');
    setPhase('error');
  }

  // ---- GPS ------------------------------------------------------------------
  function getGeo() {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        reject('NO_LOCATION'); return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        (err) => {
          if (err && err.code === 1) reject('DENIED');       // PERMISSION_DENIED
          else if (err && err.code === 3) reject('UNAVAILABLE'); // TIMEOUT
          else reject('UNAVAILABLE');                          // POSITION_UNAVAILABLE
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
    });
  }

  // ---- camera ---------------------------------------------------------------
  async function startCamera() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw 'NO_CAM';
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
    } catch (e) {
      const n = (e && e.name) || '';
      if (n === 'NotAllowedError' || n === 'SecurityError') throw 'CAM_DENIED';
      if (n === 'NotFoundError' || n === 'OverconstrainedError') throw 'NO_CAM';
      throw 'NO_CAM';
    }
    // If the effect was torn down while getUserMedia was still awaiting, stop
    // the just-acquired track NOW so the camera light can't leak on.
    if (!aliveRef.current) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /**/ }
      throw 'CANCELLED';
    }
    streamRef.current = stream;
    const v = videoRef.current;
    if (!v) throw 'NO_CAM';
    v.srcObject = stream;
    v.setAttribute('playsinline', '');
    v.muted = true;
    try { await v.play(); } catch (_e) { /* autoplay quirks — frames still flow */ }
    // Wait until we actually have pixels.
    if (v.readyState < 2) {
      await new Promise((res) => {
        const on = () => { v.removeEventListener('loadeddata', on); res(); };
        v.addEventListener('loadeddata', on);
        setTimeout(res, 4000);
      });
    }
  }

  // ---- capture the still and punch -----------------------------------------
  async function captureAndPunch() {
    if (capturedRef.current || !aliveRef.current) return;
    capturedRef.current = true;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    setPhase('sending');
    setStatus(T.cap_sending);

    const v = videoRef.current;
    const vw = v?.videoWidth || 0;
    const vh = v?.videoHeight || 0;
    if (!vw || !vh) { fail('MISSING_IMAGE'); return; }

    const scale = Math.min(1, MAX_EDGE / Math.max(vw, vh));
    const cw = Math.round(vw * scale);
    const ch = Math.round(vh * scale);
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    // Draw the RAW frame (CSS mirroring on the <video> does not affect canvas),
    // so the still matches the un-mirrored enrolment image the sidecar expects.
    canvas.getContext('2d').drawImage(v, 0, 0, cw, ch);

    let image_b64;
    try {
      const url = canvas.toDataURL('image/jpeg', JPEG_Q);
      image_b64 = url.slice(url.indexOf(',') + 1);
    } catch (_e) { fail('MISSING_IMAGE'); return; }

    // Release the camera light before the (blocking) round-trip.
    stopCamera();

    consumedRef.current = true; // token is about to be spent
    const geo = geoRef.current || {};
    const res = await api.webPunch({
      token: tokenRef.current,
      lat: geo.lat,
      lng: geo.lng,
      accuracy: geo.accuracy,
      cue: cueRef.current,
      image_b64,
    });

    if (!aliveRef.current) return;
    if (res && res.ok) {
      const isIn = !!(res.check_in || res.check_in_time) &&
        !(res.check_out || res.check_out_time);
      setResult({ isIn, name: res.name || '' });
      setPhase('done');
    } else {
      fail((res && res.error) || 'SERVER_ERROR');
    }
  }

  // ---- per-frame tracking loop ---------------------------------------------
  function loop() {
    if (capturedRef.current || !aliveRef.current) return;
    const v = videoRef.current;
    const lm = landmarkerRef.current;
    const now = performance.now();

    if (v && lm && v.readyState >= 2) {
      let det = null;
      try { det = lm.detectForVideo(v, now); } catch (_e) { det = null; }
      const marks = det && det.faceLandmarks && det.faceLandmarks[0];
      const yp = marks ? yawPitch(marks) : null;

      if (yp) {
        if (!baseRef.current) {
          // Calibrate a neutral baseline from the first steady frames.
          const acc = baseAcc.current;
          if (!acc.startedAt) acc.startedAt = now;
          acc.yaw += yp.yaw; acc.pitch += yp.pitch; acc.n += 1;
          setStatus(T.cap_align);
          if (acc.n >= BASELINE_MIN_SAMPLES && now - acc.startedAt >= BASELINE_MS) {
            baseRef.current = { yaw: acc.yaw / acc.n, pitch: acc.pitch / acc.n };
            moveStartRef.current = now;
            setStatus(T.cap_follow_cue);
          }
        } else {
          const dYaw = yp.yaw - baseRef.current.yaw;
          const dPitch = yp.pitch - baseRef.current.pitch;
          if (matchesCue(cueRef.current, dYaw, dPitch)) {
            setStatus(T.cap_hold_still);
            captureAndPunch();
            return;
          }
          if (now - moveStartRef.current > MOVE_FALLBACK_MS) {
            captureAndPunch();
            return;
          }
        }
      }
    }

    // Safety net: never hang if the face is never found / baseline never sets.
    if (now - trackStartRef.current > HARD_CAP_MS) {
      captureAndPunch();
      return;
    }
    rafRef.current = requestAnimationFrame(loop);
  }

  // ---- one-shot setup on mount ---------------------------------------------
  useEffect(() => {
    aliveRef.current = true;
    let cancelled = false;

    (async () => {
      const token = getToken();
      tokenRef.current = token;
      if (!token) { fail('TOKEN_MISSING'); return; }

      // 2) GPS
      setStatus(T.cap_getting_location);
      try { geoRef.current = await getGeo(); }
      catch (code) { if (!cancelled) fail(code); return; }
      if (cancelled) return;

      // 3) camera
      setStatus(T.cap_starting_camera);
      try { await startCamera(); }
      catch (code) { if (!cancelled) fail(code); return; }
      if (cancelled) return;

      // 4) challenge cue + 5) MediaPipe — kick off together.
      setStatus(T.cap_loading_face);
      const cuePromise = api.webChallenge(token);
      let tracker;
      try { tracker = await createFaceTracker(); }
      catch (_e) { if (!cancelled) fail('FACE_SERVICE_DOWN'); return; }
      if (cancelled) { try { tracker.close(); } catch (_e) { /**/ } return; }
      landmarkerRef.current = tracker;

      const chal = await cuePromise;
      if (cancelled) return;
      if (!chal || !chal.ok) { fail((chal && chal.error) || 'SERVER_ERROR'); return; }
      cueRef.current = chal.cue;
      setCue(chal.cue);

      // 6) run the detection loop
      setPhase('tracking');
      setStatus(T.cap_align);
      trackStartRef.current = performance.now();
      rafRef.current = requestAnimationFrame(loop);
    })();

    return () => {
      cancelled = true;
      aliveRef.current = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- render ---------------------------------------------------------------
  if (phase === 'error') {
    const ui = errorUI(errCode);
    return (
      <Screen>
        <div className="err">
          <div className="err__icon bad">😕</div>
          <div className="err__title">{ui.title_uz}</div>
          <div className="err__hint">{ui.hint_uz}</div>
          <div style={{ width: '100%', maxWidth: 320, marginTop: 6 }}>
            {/* A dead/spent token can never succeed on reload → send them back
                to Telegram to re-mint; only offer retry for transient (GPS /
                camera / face) errors where the 120s token is still valid. */}
            {consumedRef.current || ui.recover === 'home' ? (
              <Button onClick={backToTelegram}>{T.btn_back_to_tg}</Button>
            ) : (
              <Button onClick={() => window.location.reload()}>{T.btn_retry}</Button>
            )}
          </div>
        </div>
      </Screen>
    );
  }

  if (phase === 'done') {
    const isIn = result?.isIn;
    return (
      <Screen>
        <div className="res">
          <div className={`seal ${isIn ? 'ok' : 'io'}`}>{isIn ? '✓' : '👋'}</div>
          <div className="res-h">{T.cap_success_h}</div>
          <div className="res-s">{isIn ? T.cap_success_in : T.cap_success_out}</div>
          <div style={{ width: '100%', maxWidth: 320, marginTop: 14 }}>
            <Button onClick={backToTelegram}>{T.btn_back_to_tg}</Button>
          </div>
        </div>
      </Screen>
    );
  }

  // init + tracking + sending → the live camera view.
  const showCue = phase === 'tracking' && cue;
  return (
    <div className="cap">
      <div className="cap__video-wrap">
        <video ref={videoRef} className="cap__video" playsInline autoPlay muted />
        <div className="cap__oval" />
        {showCue ? (
          <div className="cap__cue">
            <div className="cue-arrow" aria-hidden="true">{cueArrow(cue)}</div>
            <div className="cue cue-big">{cueText(cue)}</div>
          </div>
        ) : null}
        <div className="cap__status">
          {phase === 'sending' ? (
            <Spinner size="md" inline />
          ) : null}
          <div>{status}</div>
        </div>
      </div>
    </div>
  );
}
